/**
 * The audit workspace â€” the frame every audit-scoped screen sits in.
 *
 * The audit, not the file, is the unit of analysis, so it is a PLACE: one layout
 * route holding the context bar and tab strip, with each screen nested via
 * `<Outlet/>`. Both are read from one fetch here, so the bar cannot fall out of
 * step with the tab under it.
 *
 * Readiness is a list of named blockers, stated once above the Outlet so no tab
 * has to re-fetch the audit to answer "why can't this be reported".
 */

import { useCallback } from 'react';
import {
	Link,
	Outlet,
	useLocation,
	useOutletContext,
	useParams,
} from 'react-router-dom';
import { Tab, Tabs } from '@mui/material';
import { useAuditQuery } from '../../queries/audits';
import { useFollowRecordModule, useModule } from '../modules/useModule';
import { auditPath, auditsCrumbs } from '../modules/flow';
import { withModule } from '../modules/modules';
import { Note } from '../../shared/ui';
import { count } from '../../utils/format';
import { financialYearLabel } from '../../utils/period';
import { ErrorState, LoadingState } from '../shared/states';
import type { AuditDetail } from '../../schemas';
import type { Crumb } from '../../provider/pageTitle';

/**
 * What every nested tab receives through `<Outlet context={â€¦}/>`.
 *
 * `useAuditContext` is the only way a tab reaches its audit, and `refresh()` the
 * only way it may change what the context bar and other tabs see. A tab that
 * mutates and then re-fetches only its own slice leaves the two disagreeing.
 */
export interface AuditContext {
	audit: AuditDetail;
	refresh: () => Promise<void>;
	/** Clients › {client} › {Module} › {audit} — the trail every page of this
	 *  audit starts with. The module is the audit's own. */
	crumbs: Crumb[];
}

export function useAuditContext(): AuditContext {
	return useOutletContext<AuditContext>();
}

/** The FY a period start falls in — same rule `AuditsList.tsx` reads its
 *  filter options by, duplicated rather than imported because that copy is a
 *  local, unexported function of that screen. */
function periodFYLabel(periodStart: string): string | null {
	const m = /^(\d{4})-(\d{2})/.exec(periodStart);
	if (!m) return null;
	const year = Number(m[1]);
	const month = Number(m[2]);
	return financialYearLabel(month >= 4 ? year : year - 1);
}

export default function AuditWorkspace() {
	const { auditId = '' } = useParams();
	const { pathname } = useLocation();
	const { data, error, isPending, refetch } = useAuditQuery(auditId ?? null);
	/* The audit's module wins over the URL's, for this page and every tab. */
	useFollowRecordModule(data?.module);
	const { link } = useModule();

	/* Narrowed to `Promise<void>`: callers want the awaiting, not React Query's
     result object, which would otherwise leak into every screen. */
	const refresh = useCallback(async () => {
		await refetch();
	}, [refetch]);
	if (isPending) return <LoadingState label="Loading the audit…" />;
	if (error) {
		return (
			<ErrorState
				error={error}
				onRetry={refetch}
				hint={
					<>
						Every audit is reachable from{' '}
						<a className="link" href={link('/audits')}>
							Audits
						</a>
						.
					</>
				}
			/>
		);
	}
	if (!data) return null;

	const base = auditPath(data);
	const tabs: Array<{ to: string; label: string; pill?: number }> = [
		// { to: `${base}/data`, label: 'Data' },
		{ to: `${base}/analyses`, label: 'Analyses' },
		/* Exceptions and Report are hidden for now; their routes stay reachable
       by direct link. */
		// { to: `${base}/exceptions`, label: 'Exceptions', pill: data.openExceptions },
		// { to: `${base}/report`, label: 'Report' },
		{ to: `${base}/details`, label: 'Details' },
	];
	const activeTab =
		tabs.find((tab) => pathname.startsWith(tab.to))?.to ?? false;
	/* Inside one analysis (`/analyses/26`, `/analyses/<slug>`) the page has its
     own header and a back button; the audit's tab bar would only repeat
     where the reader already is. */
	const insideAnalysis = pathname.startsWith(`${base}/analyses/`);

	return (
		<div className="stack workspace">
			{!insideAnalysis && (
				<div className="ctx">
					<div className="ctx-who">
						<div className="ctx-id">
							<span className="client">{data.clientName}</span>
							<span className="audit">
								<span>{data.name}</span>
								<span className="sep" aria-hidden="true">
									·
								</span>
								<span>
									{periodFYLabel(data.periodStart) ??
										data.periodStart}
								</span>
							</span>
						</div>
					</div>

					<Tabs
						value={activeTab}
						aria-label="Audit sections"
						variant="scrollable"
						scrollButtons="auto"
					>
						{tabs.map((tab) => (
							<Tab
								key={tab.to}
								value={tab.to}
								component={Link}
								to={link(tab.to)}
								label={
									<span
										className="row"
										style={{
											gap: 'var(--s2)',
											alignItems: 'center',
										}}
									>
										{tab.label}
										{tab.pill !== undefined &&
											tab.pill > 0 && (
												<span
													className={`n${data.blockingExceptions > 0 ? ' bad' : ''}`}
												>
													{count(tab.pill)}
												</span>
											)}
									</span>
								}
							/>
						))}
					</Tabs>
				</div>
			)}

			{!data.readiness.ready && (
				<Note tone="warn" title="Before the report can be issued" row>
					{data.readiness.blockers.join(' ')}
				</Note>
			)}

			<Outlet
				context={
					{
						audit: data,
						refresh,
						crumbs: [
							...auditsCrumbs(
								{ id: data.fkClientId, name: data.clientName },
								data.module,
							),
							{
								label: data.name,
								to: withModule(`${base}/analyses`, data.module),
							},
						],
					} satisfies AuditContext
				}
			/>
		</div>
	);
}
